import styled from '@emotion/styled';
import { Trans, t } from '@lingui/macro';
import { Avatar } from 'components/avatar';
import { Card } from 'components/card';
import { Chip } from 'components/chip';
import { Text } from 'components/text';
import { Button } from 'components/button';
import { type LoginUserListItemDto } from 'features/auth/types';

type UserAccountListProps = {
  users: LoginUserListItemDto[];
  onSelect: (id: string) => void;
};

export const UserAccountList = ({ users, onSelect }: UserAccountListProps) => {
  return (
    <Container>
      <HeaderSection>
        <Text variant="headlineXs" weight="semiBold">
          <Trans>Select your account</Trans>
        </Text>
        <Text variant="bodySm" color="secondary">
          <Trans>
            You are associated with multiple accounts. Please select which account to sign in with.
          </Trans>
        </Text>
      </HeaderSection>
      <UserGrid>
        {users.map(user => (
          <UserGridItem key={user.id}>
            <Card
              header={{ title: user.name }}
              icon={<Avatar letters={user.name} size="xxl" />}
              actions={{
                slot: (
                  <Button variant="secondary" fullWidth size="md" onClick={() => onSelect(user.id)}>
                    <Trans>Select account</Trans>
                  </Button>
                ),
              }}
            >
              <UserDetail>
                <Text variant="bodySm">{user.organization.name}</Text>
                <div>
                  <Chip
                    label={user.organization.type === 'supplier' ? t`Supplier` : t`Designer`}
                    color={user.organization.type === 'supplier' ? 'blue' : 'brand'}
                    isBackgroundFilled
                    isTooltip={false}
                  />
                </div>
              </UserDetail>
            </Card>
          </UserGridItem>
        ))}
      </UserGrid>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};
`;

const UserGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${p => p.theme.s.md};
`;

const UserGridItem = styled.div`
  flex: 1 1 220px;
`;

const UserDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};
`;
